import { useCallback, useEffect, useRef, useState } from 'react'

export type CameraState = 'idle' | 'starting' | 'ready' | 'denied' | 'error'

export function useCamera() {
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [state, setState] = useState<CameraState>('idle')
  const [cameraOn, setCameraOn] = useState(true)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [deviceId, setDeviceId] = useState('')
  const streamRef = useRef<MediaStream | null>(null)

  const refreshDevices = useCallback(async () => {
    const available = await navigator.mediaDevices.enumerateDevices()
    setDevices(available.filter((device) => device.kind === 'videoinput'))
  }, [])

  const acquire = useCallback(async (selectedDeviceId?: string) => {
    const media = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        ...(selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : {}),
        width: { ideal: 960, max: 1280 },
        height: { ideal: 540, max: 720 },
        frameRate: { ideal: 24, max: 30 },
      },
    })
    const track = media.getVideoTracks()[0]
    if (track) {
      track.contentHint = 'motion'
      setDeviceId(track.getSettings().deviceId ?? selectedDeviceId ?? '')
    }
    return media
  }, [])

  const start = useCallback(async () => {
    if (streamRef.current) return streamRef.current
    setState('starting')
    try {
      const media = await acquire()
      streamRef.current = media
      setStream(media)
      setCameraOn(true)
      setState('ready')
      await refreshDevices()
      return media
    } catch (error) {
      const denied =
        error instanceof DOMException &&
        ['NotAllowedError', 'PermissionDeniedError'].includes(error.name)
      setState(denied ? 'denied' : 'error')
      setCameraOn(false)
      return null
    }
  }, [acquire, refreshDevices])

  const selectDevice = useCallback(async (selectedDeviceId: string) => {
    if (!selectedDeviceId || selectedDeviceId === deviceId) return
    setState('starting')
    try {
      const next = await acquire(selectedDeviceId)
      const previous = streamRef.current
      streamRef.current = next
      setStream(next)
      setCameraOn(true)
      setState('ready')
      previous?.getTracks().forEach((track) => track.stop())
      await refreshDevices()
    } catch {
      setState('error')
    }
  }, [acquire, deviceId, refreshDevices])

  const setEnabled = useCallback((enabled: boolean) => {
    streamRef.current?.getVideoTracks().forEach((track) => {
      track.enabled = enabled
    })
    setCameraOn(enabled)
  }, [])

  useEffect(() => {
    navigator.mediaDevices.addEventListener?.('devicechange', refreshDevices)
    return () => {
      navigator.mediaDevices.removeEventListener?.('devicechange', refreshDevices)
      streamRef.current?.getTracks().forEach((track) => track.stop())
    }
  }, [refreshDevices])

  return { stream, state, cameraOn, devices, deviceId, start, setEnabled, selectDevice }
}
